import { useEffect, useRef } from 'react';

/**
 * V3's ambient background — a slow field of drifting nodes joined by faint
 * edges.
 *
 * The canvas is always mounted; legacy.css fades it in only under
 * [data-theme="v3"], so switching theme is a CSS transition rather than a
 * remount. The draw loop is deliberately cheap:
 *
 *   · capped at 18fps rather than running at the display's refresh rate
 *   · skipped entirely while the tab is hidden, or while a theme other than V3
 *     is showing — so V1 and V2 pay nothing for it
 *   · not started at all when the visitor prefers reduced motion
 *
 * The original ran this as a bare IIFE at parse time and never cancelled the
 * animation frame. Here it is tied to the component's lifetime.
 */
const NODE_COUNT = 24;
const MAX_DIST = 160;
const FPS = 18;

export default function NeuralField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let nodes = [];
    let lastTs = 0;
    let rafId = null;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
      }));
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = ts => {
      rafId = requestAnimationFrame(draw);
      if (document.hidden) return;
      if (ts - lastTs < 1000 / FPS) return;
      lastTs = ts;
      if (document.documentElement.getAttribute('data-theme') !== 'v3') return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist >= MAX_DIST) continue;
          ctx.strokeStyle = `rgba(14,165,233,${((1 - dist / MAX_DIST) * 0.06).toFixed(3)})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(nodes[j].x, nodes[j].y);
          ctx.stroke();
        }
      }

      ctx.fillStyle = 'rgba(14,165,233,0.18)';
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    rafId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas id="v3-neural" ref={ref} aria-hidden="true" />;
}
